import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebase';

interface SubscriptionFeature {
  id: string;
  description: string;
}

interface Subscription {
  id: string;
  name: string;
  price: number;
  features: SubscriptionFeature[];
  isDefault?: boolean;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Settings státuszok
  const [activeTab, setActiveTab] = useState('subscriptions');
  
  // Subscription beállítások
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isAddingSubscription, setIsAddingSubscription] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<string | null>(null);
  const [tempSubscription, setTempSubscription] = useState<Subscription>({
    id: '',
    name: '',
    price: 0,
    features: []
  });
  const [newFeature, setNewFeature] = useState('');

  // Firestore-ból betöltjük a beállításokat
  useEffect(() => {
    const loadSettings = async () => {
      if (userData?.role !== 'admin') {
        setError(t('errors.noPermission'));
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const settingsRef = doc(db, 'settings', 'subscriptions');
        const settingsSnap = await getDoc(settingsRef);
        
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setSubscriptions(data.packages || []);
        } else {
          // Ha még nincs settings dokumentum, létrehozzuk az alapértelmezett csomagokkal
          const defaultSubscriptions: Subscription[] = [
            { 
              id: 'basic', 
              name: 'Alap csomag', 
              price: 5000,
              isDefault: true,
              features: [
                { id: 'feature1', description: 'Hozzáférés az alap tananyagokhoz' },
                { id: 'feature2', description: 'Max. 2 tanulói fiók' }
              ] 
            },
            { 
              id: 'standard', 
              name: 'Standard csomag', 
              price: 10000,
              features: [
                { id: 'feature1', description: 'Hozzáférés a teljes tananyaghoz' },
                { id: 'feature2', description: 'Max. 5 tanulói fiók' },
                { id: 'feature3', description: 'Haladási riportok' }
              ] 
            },
            { 
              id: 'premium', 
              name: 'Prémium csomag', 
              price: 15000,
              features: [
                { id: 'feature1', description: 'Hozzáférés minden tartalomhoz' },
                { id: 'feature2', description: 'Korlátlan tanulói fiók' },
                { id: 'feature3', description: 'Részletes riportok és elemzések' },
                { id: 'feature4', description: 'Egyéni feladatok készítése' }
              ] 
            }
          ];
          
          setSubscriptions(defaultSubscriptions);
          await setDoc(settingsRef, { packages: defaultSubscriptions });
        }
      } catch (err) {
        console.error('Hiba a beállítások betöltésekor:', err);
        setError(t('settings.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [userData, t]);

  // Beállítások mentése
  const handleSaveSettings = async () => {
    if (userData?.role !== 'admin') {
      setError(t('errors.noPermission'));
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const settingsRef = doc(db, 'settings', 'subscriptions');
      await updateDoc(settingsRef, { packages: subscriptions });
      
      setSuccess(t('settings.saveSuccess'));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Hiba a beállítások mentésekor:', err);
      setError(t('settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // Új csomag hozzáadása
  const handleAddSubscription = () => {
    setIsAddingSubscription(true);
    
    // Generálunk egy új egyedi ID-t a csomagnak
    const newId = `package_${Date.now()}`;
    
    setTempSubscription({
      id: newId,
      name: '',
      price: 0,
      features: []
    });
  };

  // Csomag szerkesztése
  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscriptionId(subscription.id);
    setTempSubscription({...subscription});
  };

  // Ideiglenes csomag mentése
  const handleSaveSubscription = () => {
    if (!tempSubscription.name) {
      setError(t('settings.subscriptionNameRequired'));
      return;
    }
    
    if (isAddingSubscription) {
      // Új csomag hozzáadása
      setSubscriptions([...subscriptions, tempSubscription]);
    } else if (editingSubscriptionId) {
      // Meglévő csomag frissítése
      setSubscriptions(
        subscriptions.map(sub => 
          sub.id === editingSubscriptionId ? tempSubscription : sub
        )
      );
    }
    
    // Reset
    setIsAddingSubscription(false);
    setEditingSubscriptionId(null);
    setTempSubscription({
      id: '',
      name: '',
      price: 0,
      features: []
    });
  };

  // Csomag törlése
  const handleDeleteSubscription = (id: string) => {
    // Az alapértelmezett csomagokat nem lehet törölni
    const subscription = subscriptions.find(sub => sub.id === id);
    if (subscription?.isDefault) {
      setError(t('settings.cannotDeleteDefault'));
      return;
    }
    
    if (window.confirm(t('settings.confirmDeleteSubscription'))) {
      setSubscriptions(subscriptions.filter(sub => sub.id !== id));
    }
  };

  // Új funkció hozzáadása a csomaghoz
  const handleAddFeature = () => {
    if (!newFeature) return;
    
    const newFeatureObj: SubscriptionFeature = {
      id: `feature_${Date.now()}`,
      description: newFeature
    };
    
    setTempSubscription({
      ...tempSubscription,
      features: [...tempSubscription.features, newFeatureObj]
    });
    
    setNewFeature('');
  };

  // Funkció törlése a csomagból
  const handleDeleteFeature = (featureId: string) => {
    setTempSubscription({
      ...tempSubscription,
      features: tempSubscription.features.filter(f => f.id !== featureId)
    });
  };

  // Funkció szerkesztése a csomagban
  const handleUpdateFeature = (featureId: string, newDescription: string) => {
    setTempSubscription({
      ...tempSubscription,
      features: tempSubscription.features.map(f => 
        f.id === featureId ? {...f, description: newDescription} : f
      )
    });
  };

  // Ha nem admin, nem engedjük megjeleníteni a beállításokat
  if (userData?.role !== 'admin') {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {t('errors.noPermission')}
      </div>
    );
  }

  // Adatok betöltése közben loading állapot
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#309D97]"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">{t('common.settings')}</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            className="float-right"
            onClick={() => setError('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
          <button 
            className="float-right"
            onClick={() => setSuccess('')}
          >
            &times;
          </button>
        </div>
      )}
      
      {/* Beállítások navigáció */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="flex overflow-x-auto">
          <button
            className={`px-4 py-3 font-medium text-sm ${activeTab === 'subscriptions' ? 'border-b-2 border-[#309D97] text-[#309D97]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('subscriptions')}
          >
            {t('settings.subscriptions')}
          </button>
          <button
            className={`px-4 py-3 font-medium text-sm ${activeTab === 'general' ? 'border-b-2 border-[#309D97] text-[#309D97]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('general')}
          >
            {t('settings.general')}
          </button>
          <button
            className={`px-4 py-3 font-medium text-sm ${activeTab === 'security' ? 'border-b-2 border-[#309D97] text-[#309D97]' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('security')}
          >
            {t('settings.security')}
          </button>
        </div>
      </div>
      
      {/* Subscription Tab */}
      {activeTab === 'subscriptions' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-medium">{t('settings.subscriptionPackages')}</h2>
            <div>
              <button
                onClick={handleAddSubscription}
                className="bg-[#309D97] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition mr-2"
                disabled={isAddingSubscription || editingSubscriptionId !== null}
              >
                {t('settings.addSubscription')}
              </button>
              <button
                onClick={handleSaveSettings}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
                disabled={saving}
              >
                {saving ? t('settings.saving') : t('settings.saveAll')}
              </button>
            </div>
          </div>
          
          {/* Csomag szerkesztő */}
          {(isAddingSubscription || editingSubscriptionId) && (
            <div className="border rounded-lg p-4 mb-6 bg-gray-50">
              <h3 className="font-medium mb-4">
                {isAddingSubscription 
                  ? t('settings.addNewSubscription') 
                  : t('settings.editSubscription')
                }
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.packageName')}*
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                    value={tempSubscription.name}
                    onChange={(e) => setTempSubscription({...tempSubscription, name: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.packagePrice')} (Ft)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                    value={tempSubscription.price}
                    onChange={(e) => setTempSubscription({...tempSubscription, price: Number(e.target.value)})}
                    min="0"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('settings.packageFeatures')}
                  </label>
                  
                  <div className="border rounded-md p-4 bg-white mb-2">
                    {tempSubscription.features.length === 0 ? (
                      <p className="text-gray-500 text-sm">{t('settings.noFeatures')}</p>
                    ) : (
                      <ul className="space-y-2">
                        {tempSubscription.features.map(feature => (
                          <li key={feature.id} className="flex items-center">
                            <input
                              type="text"
                              className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                              value={feature.description}
                              onChange={(e) => handleUpdateFeature(feature.id, e.target.value)}
                            />
                            <button
                              onClick={() => handleDeleteFeature(feature.id)}
                              className="ml-2 text-red-500 hover:text-red-700"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  <div className="flex">
                    <input
                      type="text"
                      className="flex-grow px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-[#309D97]"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder={t('settings.addFeaturePlaceholder')}
                    />
                    <button
                      onClick={handleAddFeature}
                      className="bg-[#309D97] text-white px-4 py-2 rounded-r-md hover:bg-opacity-90"
                    >
                      {t('settings.addFeature')}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => {
                    setIsAddingSubscription(false);
                    setEditingSubscriptionId(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveSubscription}
                  className="px-4 py-2 bg-[#309D97] text-white rounded-md hover:bg-opacity-90"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          )}
          
          {/* Csomagok listája */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {subscriptions.map(subscription => (
              <div key={subscription.id} className="border rounded-lg overflow-hidden bg-white">
                <div className="px-4 py-3 bg-gray-100 border-b flex justify-between items-center">
                  <h3 className="font-medium">{subscription.name}</h3>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEditSubscription(subscription)}
                      className="text-blue-600 hover:text-blue-800"
                      disabled={isAddingSubscription || editingSubscriptionId !== null}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteSubscription(subscription.id)}
                      className={`${subscription.isDefault ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-800'}`}
                      disabled={subscription.isDefault || isAddingSubscription || editingSubscriptionId !== null}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="px-4 py-3">
                  <div className="text-lg font-bold text-[#309D97] mb-2">
                    {subscription.price.toLocaleString()} Ft<span className="text-sm font-normal text-gray-500">/hó</span>
                  </div>
                  <ul className="space-y-2">
                    {subscription.features.map(feature => (
                      <li key={feature.id} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>{feature.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Általános beállítások Tab */}
      {activeTab === 'general' && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">{t('settings.generalSettingsComing')}</p>
        </div>
      )}
      
      {/* Biztonsági beállítások Tab */}
      {activeTab === 'security' && (
        <div className="bg-white shadow rounded-lg p-6">
          <p className="text-gray-500">{t('settings.securitySettingsComing')}</p>
        </div>
      )}
    </div>
  );
};

export default Settings; 